import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

/**
 * Hook to handle auth session changes and token expiry.
 * Shows a toast when session expires and redirects to auth page.
 */
export function useAuthSession() {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  useEffect(() => {
    // Skip auth check for public routes
    const publicRoutes = ['/', '/auth', '/reset-password', '/pricing', '/privacy-policy', '/terms-of-service'];
    const isPublicRoute = publicRoutes.some(route => location.pathname === route);
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'TOKEN_REFRESHED') {
        console.log('Auth token refreshed');
      }
      
      if (event === 'SIGNED_OUT') {
        // Only redirect if not already on a public route
        if (!isPublicRoute) {
          toast({
            title: 'Session Ended',
            description: 'Please sign in again to continue.',
          });
          navigate('/auth');
        }
      }

      // Handle token refresh failures
      if (event === 'TOKEN_REFRESHED' && !session) {
        if (!isPublicRoute) {
          toast({
            title: 'Session Expired',
            description: 'Your session has expired. Please sign in again.',
            variant: 'destructive',
          });
          navigate('/auth');
        }
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [navigate, location.pathname, toast]);
}

export default useAuthSession;